import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar as CalendarIcon,
  Plus,
  Clock,
  User,
  ChevronLeft,
  ChevronRight,
  Filter,
  Sparkles,
  Search,
} from 'lucide-react';
import { Button } from '../components/common/Button';
import { AppointmentDrawer, type AppointmentDrawerData } from '../components/calendar/AppointmentDrawer';
import { useStaff, useServices, useCustomers, useBaseCurrency } from '../db/hooks/useLookupData';
import { useDatabase } from '../db/DatabaseProvider';
import { usePOSStore } from '../stores/pos.store';
import { dayjs } from '../lib/dayjs';
import type { TransactionDocType, CustomerPackageDocType, ServiceDocType } from '../db/schemas';

export function Calendar() {
  const db = useDatabase();
  const [selectedDate, setSelectedDate] = useState<string>(() => dayjs().format('YYYY-MM-DD'));
  const [selectedStaffId, setSelectedStaffId] = useState<string>('all');
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentDrawerData | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { staff = [] } = useStaff();
  const { services = [] } = useServices();
  const { customers = [] } = useCustomers();
  const { currencySymbol, baseCurrency } = useBaseCurrency();
  const openPOS = usePOSStore((s) => s.open);

  const [transactions, setTransactions] = useState<TransactionDocType[]>([]);
  const [customerPackages, setCustomerPackages] = useState<CustomerPackageDocType[]>([]);

  // Subscribe to real RxDB transactions and package attentions
  useEffect(() => {
    if (!db) return;

    const subTx = db.transactions.find({ selector: { is_deleted: false } }).$.subscribe((docs) => {
      setTransactions(docs.map((d) => d.toJSON() as any));
    });

    const subCp = db.customer_packages.find({ selector: { is_deleted: false } }).$.subscribe((docs) => {
      setCustomerPackages(docs.map((d) => d.toJSON() as any));
    });

    return () => {
      subTx.unsubscribe();
      subCp.unsubscribe();
    };
  }, [db]);

  // Dynamically build appointments array from real database transactions and package session redemptions
  const realAppointments: AppointmentDrawerData[] = useMemo(() => {
    const list: AppointmentDrawerData[] = [];
    const targetDateStr = dayjs(selectedDate).format('YYYY-MM-DD');

    // 1. Map POS Transactions executed on selectedDate
    transactions.forEach((tx) => {
      const rawFecha = tx.date || tx.created_at;
      if (!rawFecha) return;
      const txDateStr = dayjs(rawFecha).format('YYYY-MM-DD');
      if (txDateStr !== targetDateStr) return;

      const client = customers.find((c) => c.id === tx.customer_id);
      const items = tx.items || [];

      items.forEach((item, idx) => {
        const serv = services.find((s) => s.id === item.service_id) || {
          id: item.service_id || `s-${idx}`,
          concept: item.concept,
          currency_price: item.unit_price || 0,
          duration_minutes: 30,
        };

        list.push({
          id: `apt-tx-${tx.id}-${idx}`,
          customerId: tx.customer_id || undefined,
          customerName: tx.customer_name || client?.name || 'Cliente Mostrador',
          customerEmail: client?.email || undefined,
          customerPhone: client?.phone || undefined,
          service: serv as ServiceDocType,
          operatorId: item.operator_id || staff[0]?.id || 'staff-unassigned',
          fechaHora: rawFecha,
          duracionMinutos: serv.duration_minutes || 30,
          status: 'completed',
          amount: item.subtotal || tx.total_amount || 0,
          transactionId: tx.id,
        });
      });
    });

    // 2. Map Registered Attentions / Sessions from customer_packages executed on selectedDate
    customerPackages.forEach((cp) => {
      (cp.attentions || []).forEach((atn) => {
        const atnDate = atn.attention_date;
        if (!atnDate) return;
        const atnDateStr = dayjs(atnDate).format('YYYY-MM-DD');
        if (atnDateStr !== targetDateStr) return;

        const client = customers.find((c) => c.id === cp.customer_id);
        const serv = services.find((s) => s.id === atn.service_id) || {
          id: atn.service_id,
          concept: 'Atención de Paquete',
          currency_price: 0,
          duration_minutes: 30,
        };

        const assignedStaffId = atn.staff_id || staff[0]?.id || 'staff-unassigned';

        list.push({
          id: `apt-atn-${atn.id}`,
          customerId: cp.customer_id,
          customerName: client?.name || 'Cliente Paquete',
          customerEmail: client?.email || undefined,
          customerPhone: client?.phone || undefined,
          service: serv as ServiceDocType,
          operatorId: assignedStaffId,
          fechaHora: atnDate,
          duracionMinutos: serv.duration_minutes || 30,
          status: 'completed',
          amount: 0, // Prepaid package session (client pays €0.00 at redemption time)
          transactionId: cp.transaction_id || undefined,
        });
      });
    });

    return list;
  }, [selectedDate, transactions, customerPackages, customers, services, staff]);

  const filteredAppointments = useMemo(() => {
    return realAppointments.filter((apt) => {
      if (selectedStaffId !== 'all' && apt.operatorId !== selectedStaffId) {
        return false;
      }
      return true;
    });
  }, [realAppointments, selectedStaffId]);

  const handleOpenAppointment = (apt: AppointmentDrawerData) => {
    setSelectedAppointment(apt);
    setIsDrawerOpen(true);
  };

  const handleNewSaleDirect = () => {
    openPOS();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in">
      
      {/* ── Top Header Banner ── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-surface border border-border p-6 rounded-2xl shadow-xs">
        <div>
          <div className="flex items-center gap-2 text-brand-orange font-bold text-xs uppercase tracking-wider mb-1">
            <CalendarIcon className="w-4 h-4 text-brand-orange" />
            Agenda & Citas
          </div>
          <h1 className="text-2xl font-black text-text-primary">
            Gestión de Citas y Turnos
          </h1>
          <p className="text-xs text-text-secondary">
            Selecciona una cita para abrir el Drawer de status, cobrar o reagendar.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="primary"
            size="md"
            icon={Plus}
            onClick={handleNewSaleDirect}
            className="bg-brand-orange hover:bg-brand-orange/90 text-white font-extrabold shadow-lg shadow-brand-orange/20"
          >
            Nueva Venta POS
          </Button>
        </div>
      </div>

      {/* ── Filter Bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-4 bg-surface-secondary/40 border border-border p-4 rounded-2xl">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedDate(dayjs(selectedDate).subtract(1, 'day').format('YYYY-MM-DD'))}
            className="p-2 rounded-xl border border-border bg-surface hover:bg-surface-tertiary transition-colors text-text-secondary"
            title="Día anterior"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-1.5 rounded-xl border border-border bg-surface text-sm font-bold text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20"
          />

          <button
            onClick={() => setSelectedDate(dayjs(selectedDate).add(1, 'day').format('YYYY-MM-DD'))}
            className="p-2 rounded-xl border border-border bg-surface hover:bg-surface-tertiary transition-colors text-text-secondary"
            title="Día siguiente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          <span className="text-xs font-extrabold text-brand-sky capitalize hidden sm:inline-block">
            {dayjs(selectedDate).format('dddd D [de] MMMM, YYYY')}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-text-tertiary" />
          <select
            value={selectedStaffId}
            onChange={(e) => setSelectedStaffId(e.target.value)}
            className="px-3 py-1.5 rounded-xl border border-border bg-surface text-xs font-bold text-text-primary focus:border-brand-sky"
          >
            <option value="all">Todo el equipo ({staff.length})</option>
            {staff.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Appointments Schedule Grid ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredAppointments.map((apt) => {
          const staffMember = staff.find((p) => p.id === apt.operatorId);

          return (
            <div
              key={apt.id}
              onClick={() => handleOpenAppointment(apt)}
              className="bg-surface border border-border/80 hover:border-brand-sky/60 p-5 rounded-2xl shadow-xs transition-all cursor-pointer group hover:shadow-lg space-y-4"
            >
              <div className="flex items-center justify-between">
                <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-extrabold uppercase tracking-wider border ${
                  apt.status === 'completed'
                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20'
                    : 'bg-brand-sky/10 text-brand-sky border-brand-sky/20'
                }`}>
                  {apt.status}
                </span>

                <span className="text-xs font-extrabold text-text-primary">
                  {currencySymbol}{apt.amount.toFixed(2)} {baseCurrency}
                </span>
              </div>

              <div>
                <h4 className="font-extrabold text-base text-text-primary group-hover:text-brand-sky transition-colors">
                  {apt.customerName}
                </h4>
                <p className="text-xs text-text-secondary font-semibold">
                  {apt.service.concept}
                </p>
              </div>

              <div className="pt-3 border-t border-border/50 flex items-center justify-between text-xs text-text-tertiary">
                <span className="flex items-center gap-1 font-semibold">
                  <Clock className="w-3.5 h-3.5 text-brand-sky" />
                  {dayjs(apt.fechaHora).format('HH:mm')} ({apt.duracionMinutos} min)
                </span>

                <span className="flex items-center gap-1 font-semibold">
                  <User className="w-3.5 h-3.5 text-brand-orange" />
                  {staffMember ? staffMember.name : 'Sin operador'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Integrated Appointment Drawer ── */}
      <AppointmentDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        appointment={selectedAppointment}
      />
    </div>
  );
}
